import React, { useEffect, useState } from 'react';
import * as Yup from "yup";
import { Col, Row, Label, Button, Form, Input, CardFooter, Card, CardBody, CardHeader } from "reactstrap";
import { useQuery } from 'react-query';
import AsyncSelect from "react-select/async";
import ReportDetailsHeader from 'Components/ReportDetailsHeader';
import { queryKeyes } from 'shared/queryKeys';
import { Get } from 'VesselMaster/vesselMaster.hooks';
import { useFormik } from 'formik';
import { checkForNaN, checkValidArray, dateTimeFormat, handleServerResponse, isFormUnchanged, saveEditForm, searchCountries, searchPorts, useSafeFormikContext } from 'GenericForms/Helper';
import Loading from 'Components/Loading';
import ErrorComponent from 'Components/ErrorComponent';
import apiGlobal from 'global/api.global';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import { commonValidationMessages } from 'Components/ValidationErrorMessages';
import { queryClient } from 'react-query/queryClient';
import env from 'environment_system/env_system';
import { PortConstant } from 'shared/constants';
import { customStyle } from 'shared/CommonCSS';
import ErrorTooltip from 'Components/ErrorTooltip';
import Select from 'react-select';
import { useDocumentTitle } from 'Components/useDocument.hooks';
import LubeOilROBDetailsTable from './LubeOilROBDetailsTable';
import Message from 'Components/Message';
import { RootState } from 'index';
import { useSelector, useDispatch } from 'react-redux';
import { getCurrentReportAction } from 'Store/Generic/ReportingSlice';
import { getAuthData } from 'utils/auth/authUtils';
import { useEditReport } from 'VesselReportingInfo/EditCompletedReports/EditReportProvider';

interface LubeOilDebunkeringType {
    toggleDynamicTabs?: any,
    setErrorMessage?: any,
}

const LubeOilDebunkeringComponent = ({
    toggleDynamicTabs,
    setErrorMessage,
}: LubeOilDebunkeringType) => {
    /** State variables */
    const { editReportBool, setPreviousTabIndex, previousEnggTabIndex, discardwarning } = useEditReport();
    useDocumentTitle('Lube Oil Debunkering Report - ecoSAIL');
    const { VesselID, ReportID, VoyageID, CurrentReport, PreviousReport, reportingLoading, ConfigurationLimits, reportingError } = useSelector((state: RootState) => state.Reporting);
    const dispatch = useDispatch();
    const lubeOilDebunkeringCurrentReport = CurrentReport?.data?.chief_engineer?.lube_oil_debunkering ?? null;
    const lubeOilROBCurrentReport = CurrentReport?.data?.chief_engineer?.reporting_lube_oil_rob ?? null;
    const lubeOilROBPreviousReport = PreviousReport?.data?.chief_engineer?.reporting_lube_oil_rob ?? null;
    const currentVesselReport = CurrentReport?.data?.vessel_reporting_information ?? null;
    const previousVesselReport = PreviousReport?.data?.vessel_reporting_information ?? null;
    const [otherPort, setOtherPort] = useState(false);
    const [addOtherPort, setAddOtherPort] = useState(false);
    const responseArray: any = [];
    const formikContext = useSafeFormikContext<any>();
    const editValues = formikContext?.values;
    const setEditValues = formikContext?.setValues;
    const editInitialValues = formikContext?.initialValues;
    const editSubmit = formikContext?.submitForm;
    const editIsSubmitting = formikContext?.isSubmitting;
    const VesselLubeOils = ConfigurationLimits?.vessel_lube_oil_list || [];
    /** State variables end */

    /** Queries */
    /** Other Ports from master */
    const { data: OtherPortsObject, isLoading: OtherPortsLoading }:
        { data: any, isLoading: any, isError: any } = useQuery(
            [queryKeyes.masters.OtherPortMaster.key, VesselID, ReportID],
            async () => {
                return await Get(queryKeyes.masters.OtherPortMaster.url());
            },
            { staleTime: Infinity }
        )
    /** Queries end */

    const calculateLOROB = (lubeOil: any) => {
        const lorob = lubeOilROBCurrentReport?.find(
            (item: any) => item?.vessel_lube_oil === lubeOil?.vessel_lube_oil
        );

        const prevRob = lubeOilROBPreviousReport?.find(
            (item: any) => item?.vessel_lube_oil === lubeOil?.vessel_lube_oil
        );

        const rob = parseFloat(prevRob?.rob || '0');
        const debunkered = parseFloat(lubeOil?.debunkered || '0');
        const consumed = parseFloat(lorob?.consumed || '0');
        const bunkered = parseFloat(lorob?.bunkered || '0');
        const adjusted = parseFloat(lorob?.adjusted || '0');
        if (editReportBool) {
            const EditedBunkering = editValues?.chief_engineer?.lube_oil_bunkering?.find(
                (item: any) => item?.vessel_lube_oil === lubeOil?.vessel_lube_oil
            )?.bunkered;
            const EditedConsumption = editValues?.chief_engineer?.lube_oil_consumption?.find(
                (item: any) => item?.vessel_lube_oil === lubeOil?.vessel_lube_oil
            )?.consumed;
            const EditedAdjustment = editValues?.chief_engineer?.lube_oil_consumption?.find(
                (item: any) => item?.vessel_lube_oil === lubeOil?.vessel_lube_oil
            ).adjustment_quantity;
            const EditedDebunkered = editValues?.chief_engineer?.lube_oil_debunkering?.find(
                (item: any) => item?.vessel_lube_oil === lubeOil?.vessel_lube_oil
            )?.debunkered;
            const debunkered = parseFloat(EditedDebunkered || 0);
            const editedconsumed = parseFloat(EditedConsumption || 0);
            const editedAdjusted = parseFloat(EditedAdjustment || 0);
            const editedBunkered = parseFloat(EditedBunkering || 0);
            return parseFloat((rob + editedBunkered - editedconsumed + editedAdjusted - debunkered).toFixed(4));
        }

        const result = rob + bunkered + adjusted - consumed - debunkered;
        return parseFloat(result.toFixed(4));
    };

    const getInitialValueLubeOilDebunkering = () => {
        let obj: any[] = [];
        if (checkValidArray(editInitialValues?.chief_engineer?.lube_oil_debunkering)) {
            return editInitialValues?.chief_engineer?.lube_oil_debunkering?.map((item: any) => ({
                ...item,
                LOROB: calculateLOROB(item),
            }));
        } else if (checkValidArray(lubeOilDebunkeringCurrentReport)) {
            return lubeOilDebunkeringCurrentReport?.map((item: any) => ({
                ...item,
                LOROB: calculateLOROB(item),
            }));
        } else if (checkValidArray(VesselLubeOils)) {
            obj = VesselLubeOils.map((lube: any) => {
                const base = {
                    debunkered: 0,
                    date_of_debunkering: null as Date,
                    vessel_lube_oil: lube.id,
                    vessel_lube_oil_name: lube.vessel_lube_oil_name,
                    port_of_debunkering: null as number,
                    other_port: null as number,
                    other_port_name: null as string,
                    other_country: null as number,
                    other_country_name: null as string,
                    vessel: VesselID,
                    voyage_information: VoyageID,
                    vessel_reporting_information: ReportID,
                    port_country_name: null as string,
                };
                return {
                    ...base,
                    LOROB: calculateLOROB(base),
                };
            });
        } else {
            const base = {
                debunkered: 0,
                date_of_debunkering: null as Date,
                vessel_lube_oil: 0,
                vessel_lube_oil_name: '',
                port_of_debunkering: 0,
                vessel: VesselID,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID,
                port_country_name: null as string,
            };
            obj = [{
                ...base,
                LOROB: calculateLOROB(base),
            }];
        }

        return obj;
    };

    /** Lube oil Debunkering Formik object */
    const LubeOilDebunkeringFormik: any = useFormik({
        initialValues: {
            lubeOil: getInitialValueLubeOilDebunkering(),
        },
        validationSchema: env?.form_validation ? Yup.object().shape({
            lubeOil: Yup.array().of(
                Yup.object({
                    debunkered: Yup.string()
                        .test('is-positive', 'Enter a valid value', value => Number(value) >= 0)
                        .matches(/^\d{1,7}(\.\d{0,4})?$/, `${commonValidationMessages.before7after4}`)
                        .required(`${commonValidationMessages.required}`),
                    port_of_debunkering: Yup.number().required(commonValidationMessages.required),
                    date_of_debunkering: Yup.date()
                        .min(dateTimeFormat(previousVesselReport?.local_time, true), `${commonValidationMessages.minDateLastRecord} (${dateTimeFormat(previousVesselReport?.local_time, false, false, true)})`)
                        .max(dateTimeFormat(currentVesselReport?.local_time, true), commonValidationMessages.maxDateCurrentRecord)
                        .when('debunkered', (val: any, schema) => {
                            return val && parseFloat(val[0]) > 0
                                ? schema.required(commonValidationMessages.required)
                                : schema.nullable();
                        }),
                    other_port: Yup.string().when('$fieldAvailability', (field: any, schema) => {
                        return !(otherPort === true && OtherPortsObject)
                            ? schema.nullable()
                            : schema.required(commonValidationMessages.required);
                    }),
                    other_port_name: Yup.string().when('$fieldAvailability', (field: any, schema) => {
                        return !(addOtherPort === true || !OtherPortsObject)
                            ? schema.nullable()
                            : schema.required(commonValidationMessages.required)
                                .min(2, "Must be at least 2 characters")
                                .max(200, "Must be at most 200 characters");
                    }),
                    other_country: Yup.number().when('$fieldAvailability', (field: any, schema) => {
                        return !(addOtherPort === true || !OtherPortsObject)
                            ? schema.nullable()
                            : schema.required(commonValidationMessages.required);
                    }),
                    LOROB: Yup.string().when('debunkered', (debunkeredVal: any, schema) => {
                        return debunkeredVal && parseFloat(debunkeredVal) > 0
                            ? schema
                                .test('is-positive', 'Lube Oil ROB can not be negative', value => Number(value) >= 0)
                                .matches(/^\d{1,7}(\.\d{0,4})?$/, `${commonValidationMessages.before7after4}`)
                            : schema.nullable();
                    }),
                })
            )
        }) : null,
        onSubmit: async (values: any, actions: any) => {
            if (env?.form_validation === false || isFormUnchanged(LubeOilDebunkeringFormik.initialValues, values)) {
                toggleDynamicTabs(previousEnggTabIndex + 1);
                return;
            }
            actions.setSubmitting(true);
            await LODebunkeringSubmit(values, actions);
        }
    });

    /** useEffect start */
    useEffect(() => {
        const bunker = editInitialValues?.chief_engineer?.lube_oil_debunkering?.[0];
        if (editReportBool && bunker?.port_country_name === "Other-Other" && bunker?.other_port === null) {
            setAddOtherPort(true);
        }
    }, [editReportBool, editInitialValues?.chief_engineer?.lube_oil_debunkering]);

    useEffect(() => {
        if (editReportBool) {
            // use editValues
            if (checkValidArray(editValues?.chief_engineer?.lube_oil_debunkering)) {
                const debunkering = editValues?.chief_engineer?.lube_oil_debunkering?.[0];
                if (debunkering?.port_country_name === 'Other-Other' && !addOtherPort) {
                    setOtherPort(true);
                } else {
                    setOtherPort(false);
                }
            }
        } else {
            // use current report
            if (
                checkValidArray(lubeOilDebunkeringCurrentReport) &&
                lubeOilDebunkeringCurrentReport[0]?.port_precedence_id === PortConstant.OTHER
            ) {
                setOtherPort(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editReportBool, editValues, lubeOilDebunkeringCurrentReport]);

    useEffect(() => {
        if (Array.isArray(editValues?.chief_engineer?.lube_oil_debunkering) &&
            !isFormUnchanged(LubeOilDebunkeringFormik?.initialValues?.lubeOil, LubeOilDebunkeringFormik?.values?.lubeOil)) {
            const updatedDebunkering = LubeOilDebunkeringFormik?.values?.lubeOil?.map((debunker: any) => {
                return ({
                    ...debunker,
                    id: debunker?.id > 0 ? debunker?.id : null,
                    report_index: debunker?.report_index > 0 ? debunker?.report_index : null,
                    created_by: debunker?.created_by > 0 ? debunker?.created_by : getAuthData('user_id'),
                    modified_by: debunker?.modified_by > 0 ? debunker?.modified_by : getAuthData('user_id'),
                })
            })
            setEditValues({
                ...editValues,
                chief_engineer: {
                    ...editValues?.chief_engineer,
                    lube_oil_debunkering: updatedDebunkering
                }
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [LubeOilDebunkeringFormik?.values?.lubeOil])
    /** useEffect end */

    /** Post submit actions based on response */
    const handleResponse = () => {
        if (env?.form_validation === true) {
            setErrorMessage(null)
            toggleDynamicTabs(previousEnggTabIndex + 1);
        }
    }

    /** PUT request for LO Debunkering */
    const putLODebunkering = (value: any) => {
        const res = apiGlobal.put(`/lube_oil_debunkering/${value.id}/`, value)
        responseArray.push(res);
    }

    /** POST request for LO Debunkering */
    const postLODebunkering = (values: any) => {
        const res = apiGlobal.post(`/lube_oil_debunkering/`, values)
        responseArray.push(res);
    }

    /** Submit function for LO Bunkering */
    const LODebunkeringSubmit = async (values: any, actions: any) => {
        /** Select port of bunkering */
        /** Lube oil debunkering submit */
        if (checkValidArray(lubeOilDebunkeringCurrentReport)) {
            values.lubeOil.forEach((lube: any) => {
                putLODebunkering(lube);
            })
        } else {
            postLODebunkering(values.lubeOil)
        }
        const response = await handleServerResponse(responseArray);
        if (response === true) {
            dispatch(getCurrentReportAction(VesselID, ReportID));
            if (addOtherPort && OtherPortsObject &&
                OtherPortsObject?.filter((port: any) =>
                    port?.port_name === values?.lubeOil[0]?.other_port_name)?.length === 0) {
                await queryClient.invalidateQueries(queryKeyes.masters.OtherPortMaster.key);
            }
            handleResponse();
        } else {
            setErrorMessage(response)
        }
        actions.setSubmitting(false);
    }
    return (
        <Card className='p-0 mb-0 border-0'>
            <CardHeader className='p-2'>
                <Row>
                    <Col>
                        <h4 className="page_title mb-0">Lube Oil Debunkering</h4>
                        <p className="card-title-desc pos-start">All readings since last report</p>
                    </Col>
                    <Col>
                        <ReportDetailsHeader />
                    </Col>
                </Row>
            </CardHeader>
            {(reportingLoading ||
                OtherPortsLoading) && <Loading message='Loading required data!' />}
            {reportingError && <ErrorComponent message='Unable to load required data!' />}
            {!(reportingLoading || OtherPortsLoading) &&
                !reportingError &&
                <Form autoComplete="off" onSubmit={LubeOilDebunkeringFormik?.handleSubmit} noValidate>
                    <CardBody className='px-2 py-0'>
                        <Row>
                            {VesselLubeOils &&
                                <>
                                    <Col lg={4} className='mb-2'>
                                        <Label className='asteric mb-0' for='lubeOil.0.port_of_debunkering'>Port of debunkering</Label>
                                        <AsyncSelect
                                            name={`lubeOil.0.port_of_debunkering`}
                                            inputId={"port_of_debunkering"}
                                            cacheOptions
                                            autoFocus={true}
                                            defaultOptions
                                            loadOptions={(e: any) => searchPorts(e)}
                                            getOptionLabel={(e: any) => e?.port_country_name}
                                            getOptionValue={e => e.id}
                                            onBlur={LubeOilDebunkeringFormik?.handleBlur}
                                            onChange={(e: any) => {
                                                LubeOilDebunkeringFormik?.values?.lubeOil?.forEach((bunker: any, index: number) => {
                                                    LubeOilDebunkeringFormik?.setFieldValue(`lubeOil.${index}.port_of_debunkering`, e?.id);
                                                    LubeOilDebunkeringFormik?.setFieldValue(`lubeOil.${index}.port_country_name`, e?.port_country_name);
                                                });
                                                if (e?.precedence_id === PortConstant.OTHER && addOtherPort === false) {
                                                    setOtherPort(true);
                                                } else if (e?.precedence_id !== PortConstant.OTHER) {
                                                    setOtherPort(false);
                                                    setAddOtherPort(false);
                                                    if (LubeOilDebunkeringFormik?.values?.lubeOil && LubeOilDebunkeringFormik?.values?.lubeOil?.length > 0) {
                                                        LubeOilDebunkeringFormik?.values?.lubeOil?.forEach((bunker: any, index: any) => {
                                                            LubeOilDebunkeringFormik?.setFieldValue(`lubeOil.${index}.other_port`, null);
                                                            LubeOilDebunkeringFormik?.setFieldValue(`lubeOil.${index}.other_port_name`, null);
                                                            LubeOilDebunkeringFormik?.setFieldValue(`lubeOil.${index}.other_country`, null);
                                                            LubeOilDebunkeringFormik?.setFieldValue(`lubeOil.${index}.other_country_name`, null);
                                                        })
                                                    }
                                                }
                                            }}
                                            menuPortalTarget={document.body}
                                            defaultValue={
                                                {
                                                    id: LubeOilDebunkeringFormik?.values?.lubeOil?.[0]?.port_of_debunkering,
                                                    port_country_name: LubeOilDebunkeringFormik?.values?.lubeOil?.[0]?.port_country_name,
                                                }}
                                            noOptionsMessage={(e: any) => {
                                                if (e?.inputValue?.toString()?.length > 2) {
                                                    return 'Please select the Other option and enter the port name in the textbox provided'
                                                }
                                                return 'Please enter the first 3 characters of port name';
                                            }}
                                        />
                                        {LubeOilDebunkeringFormik?.errors.lubeOil && LubeOilDebunkeringFormik?.touched && LubeOilDebunkeringFormik?.errors?.lubeOil && LubeOilDebunkeringFormik?.touched?.lubeOil &&
                                            LubeOilDebunkeringFormik?.errors?.lubeOil[0]?.port_of_debunkering && !discardwarning &&
                                            LubeOilDebunkeringFormik?.touched?.lubeOil[0]?.port_of_debunkering && env?.form_validation === true &&
                                            <ErrorTooltip
                                                target={`port_of_debunkering`}
                                                message={LubeOilDebunkeringFormik?.errors?.lubeOil[0]?.port_of_debunkering}
                                                open={LubeOilDebunkeringFormik?.errors?.lubeOil[0]?.port_of_debunkering ? true : false}
                                            />
                                        }
                                    </Col>
                                    {otherPort === true &&
                                        <Col lg={4}>
                                            <Label className="mb-0 asteric" for='other_port_name_dropdown'>Other port</Label>
                                            <Select
                                                name="lubeOil.0.other_port"
                                                inputId='other_port_name_dropdown'
                                                options={OtherPortsObject}
                                                getOptionLabel={(e: any) => e.port_name}
                                                getOptionValue={(e: any) => e.id}
                                                onBlur={() => LubeOilDebunkeringFormik.setFieldTouched("lubeOil.0.other_port", true)}
                                                onChange={(selectedOption: any) => {
                                                    LubeOilDebunkeringFormik?.values?.lubeOil?.forEach((bunker: any, index: any) => {
                                                        LubeOilDebunkeringFormik?.setFieldValue(`lubeOil.${index}.other_port`, selectedOption.id);
                                                        LubeOilDebunkeringFormik?.setFieldValue(`lubeOil.${index}.other_port_name`, selectedOption?.port_name);
                                                        LubeOilDebunkeringFormik?.setFieldValue(`lubeOil.${index}.other_country`, selectedOption?.country);
                                                    })
                                                }}
                                                defaultValue={
                                                    {
                                                        id: LubeOilDebunkeringFormik?.values?.lubeOil?.[0]?.other_port,
                                                        port_name: LubeOilDebunkeringFormik?.values?.lubeOil?.[0]?.other_port_name
                                                    }
                                                }
                                                menuPortalTarget={document.body}
                                                styles={customStyle}
                                                noOptionsMessage={(e: any) => {
                                                    if (e?.inputValue?.toString()?.length > 2) {
                                                        return 'Please select the Other option and enter the port name in the textbox provided'
                                                    }
                                                    return 'Please enter the first 3 characters of port name';
                                                }}
                                            />
                                            {LubeOilDebunkeringFormik?.errors.lubeOil && LubeOilDebunkeringFormik?.touched &&
                                                LubeOilDebunkeringFormik?.errors?.lubeOil?.[0]?.other_port && !discardwarning &&
                                                LubeOilDebunkeringFormik?.touched?.lubeOil?.[0]?.other_port && env?.form_validation === true &&
                                                <ErrorTooltip
                                                    target={`other_port_name_dropdown`}
                                                    message={LubeOilDebunkeringFormik?.errors?.lubeOil[0]?.other_port}
                                                    open={LubeOilDebunkeringFormik?.errors?.lubeOil[0]?.other_port ? true : false}
                                                />
                                            }
                                            {!editReportBool &&
                                                <p className='link_color_blue pointer' onClick={() => {
                                                    setAddOtherPort(true);
                                                    setOtherPort(false);
                                                    LubeOilDebunkeringFormik?.values?.lubeOil?.forEach((bunker: any, index: any) => {
                                                        LubeOilDebunkeringFormik.values.lubeOil[index].other_port = null;
                                                        LubeOilDebunkeringFormik.values.lubeOil[index].other_port_name = null;
                                                        LubeOilDebunkeringFormik.values.lubeOil[index].other_country = null;
                                                        LubeOilDebunkeringFormik.values.lubeOil[index].other_country_name = null;
                                                    })
                                                }}>Click here to add new port</p>
                                            }
                                        </Col>
                                    }
                                    {(addOtherPort === true || !OtherPortsObject) &&
                                        <>
                                            <Col lg={4}>
                                                <Label className='mb-0 asteric'>Other port name</Label>
                                                <Input
                                                    onFocus={(e) => e.target.select()}
                                                    type='text'
                                                    name={`lubeOil.0.other_port_name`}
                                                    id='other_port_name'
                                                    className='form-control'
                                                    onBlur={(e: any) => {
                                                        if (LubeOilDebunkeringFormik?.values?.lubeOil && LubeOilDebunkeringFormik?.values?.lubeOil?.length > 0) {
                                                            LubeOilDebunkeringFormik?.values?.lubeOil?.forEach((bunker: any, index: any) => {
                                                                LubeOilDebunkeringFormik?.setFieldValue(`lubeOil.${index}.other_port_name`, e.target.value);
                                                            })
                                                        }
                                                    }}
                                                    defaultValue={LubeOilDebunkeringFormik?.values?.lubeOil[0]?.other_port_name}
                                                />
                                                {LubeOilDebunkeringFormik?.errors?.lubeOil && LubeOilDebunkeringFormik?.touched?.lubeOil && LubeOilDebunkeringFormik?.touched?.lubeOil[0]?.other_port_name &&
                                                    LubeOilDebunkeringFormik?.errors?.lubeOil[0]?.other_port_name && env?.form_validation === true && !discardwarning &&
                                                    <ErrorTooltip
                                                        target={`other_port_name`}
                                                        message={LubeOilDebunkeringFormik?.errors?.lubeOil[0]?.other_port_name}
                                                        open={(LubeOilDebunkeringFormik?.errors && LubeOilDebunkeringFormik?.errors?.lubeOil[0]?.other_port_name) ? true : false}
                                                    />
                                                }
                                            </Col>
                                            <Col lg={4}>
                                                <Label className='mb-0 asteric'>Other country name</Label>
                                                <AsyncSelect
                                                    name={`lubeOil.0.other_country`}
                                                    id={'other_country'}
                                                    cacheOptions
                                                    defaultOptions
                                                    loadOptions={(e: any) => searchCountries(e)}
                                                    getOptionLabel={(e: any) => e.country_name}
                                                    getOptionValue={(e: any) => e.id}
                                                    onBlur={() => {
                                                        LubeOilDebunkeringFormik?.setFieldTouched(`lubeOil.0.other_country`, true);
                                                    }}
                                                    onChange={(e: any) => {
                                                        LubeOilDebunkeringFormik?.values?.lubeOil?.forEach((bunker: any, index: any) => {
                                                            LubeOilDebunkeringFormik?.setFieldValue(`lubeOil.${index}.other_country`, e?.id);
                                                            LubeOilDebunkeringFormik?.setFieldValue(`lubeOil.${index}.other_country_name`, e?.country_name);
                                                        })
                                                    }}
                                                    defaultValue={{
                                                        id: LubeOilDebunkeringFormik?.values?.lubeOil?.[0]?.other_country,
                                                        country_name: LubeOilDebunkeringFormik?.values?.lubeOil?.[0]?.other_country_name
                                                    }}
                                                    className="select-height"
                                                    menuPortalTarget={document.body}
                                                    styles={customStyle}
                                                    noOptionsMessage={() => {
                                                        return 'Please enter the first 3 characters of country name';
                                                    }}
                                                />
                                                {LubeOilDebunkeringFormik?.errors?.lubeOil && LubeOilDebunkeringFormik?.touched?.lubeOil && LubeOilDebunkeringFormik?.touched?.lubeOil[0]?.other_country &&
                                                    LubeOilDebunkeringFormik?.errors?.lubeOil[0]?.other_country && env?.form_validation === true && !discardwarning &&
                                                    <ErrorTooltip
                                                        target={`other_country`}
                                                        message={LubeOilDebunkeringFormik?.errors?.lubeOil[0]?.other_country}
                                                        open={(LubeOilDebunkeringFormik?.errors && LubeOilDebunkeringFormik?.errors?.lubeOil[0]?.other_country) ? true : false}
                                                    />
                                                }
                                            </Col>
                                        </>
                                    }
                                    <div className="table-responsive mt-2">
                                        <table className="table mb-2">
                                            <thead className="table-light">
                                                <tr>
                                                    <th className="p-2 sr-no-width">#</th>
                                                    <th className="text-left align-middle p-2" style={{ width: '40%' }}>Lube oil</th>
                                                    <th className="text-center align-middle p-2" style={{ width: '30%' }}>Debunkered</th>
                                                    <th className="text-center align-middle p-2" style={{ width: '30%' }}>Date of Debunkering</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {VesselLubeOils && VesselLubeOils.length > 0 ? (
                                                    VesselLubeOils.map((lubeOil: any, index: number) => {
                                                        const LOROB = lubeOilROBCurrentReport?.find(
                                                            (item: any) => item.vessel_lube_oil_name === lubeOil.vessel_lube_oil_name
                                                        );
                                                        const PrevLOROB = lubeOilROBPreviousReport?.find(
                                                            (item: any) => item.vessel_lube_oil_name === lubeOil.vessel_lube_oil_name
                                                        );
                                                        const EditedBunkering = editValues?.chief_engineer?.lube_oil_bunkering?.find(
                                                            (item: any) => item.vessel_lube_oil === lubeOil.id
                                                        )
                                                        const EditedConsumption = editValues?.chief_engineer?.lube_oil_consumption?.find(
                                                            (item: any) => item.vessel_lube_oil === lubeOil.id
                                                        )?.consumed;
                                                        const EditedAdjustment = editValues?.chief_engineer?.lube_oil_consumption?.find(
                                                            (item: any) => item.vessel_lube_oil === lubeOil.id
                                                        ).adjustment_quantity;
                                                        return (
                                                            <React.Fragment key={lubeOil.id}>
                                                                <tr>
                                                                    <td className="p-2 text-center">{index + 1}</td>

                                                                    {/* Lube Oil Name + ROB Breakdown */}
                                                                    <td className="p-2 align-middle">
                                                                        <LubeOilROBDetailsTable
                                                                            lubeOilName={lubeOil.vessel_lube_oil_name}
                                                                            previousROB={parseFloat(PrevLOROB?.rob) || 0}
                                                                            debunkered={parseFloat(LubeOilDebunkeringFormik?.values?.lubeOil?.[index]?.debunkered) || 0}
                                                                            bunkered={editReportBool ? (parseFloat(EditedBunkering?.bunkered) || 0) : (parseFloat(LOROB?.bunkered) || 0)}
                                                                            consumed={editReportBool ? (parseFloat(EditedConsumption) || 0) : (parseFloat(LOROB?.consumed) || 0)}
                                                                            adjustment={editReportBool ? (parseFloat(EditedAdjustment) || 0) : (parseFloat(LOROB?.adjusted) || 0)}
                                                                            currentROB={
                                                                                calculateLOROB(LOROB)
                                                                            }
                                                                        />
                                                                    </td>
                                                                    {/* Debunkered Input */}
                                                                    <td className="p-2 text-center align-middle">
                                                                        <div className="input-group justify-content-center align-items-center">
                                                                            <Input
                                                                                onFocus={(e) => e.target.select()}
                                                                                type="text"
                                                                                className="form-control max-width-7 text-right align-middle"
                                                                                id={`debunkered-${index}`}
                                                                                name={`lubeOil.${index}.debunkered`}
                                                                                defaultValue={LubeOilDebunkeringFormik?.values?.lubeOil?.[index]?.debunkered}
                                                                                onChange={(e: any) => {
                                                                                    LubeOilDebunkeringFormik?.handleChange(e);
                                                                                    const rob = parseFloat(PrevLOROB?.rob || '0');
                                                                                    const debunkered = parseFloat(e.target.value || '0');
                                                                                    const consumed = parseFloat(LOROB?.consumed || '0');
                                                                                    const bunkered = parseFloat(LOROB?.bunkered || '0');
                                                                                    const adjusted = parseFloat(LOROB?.adjusted || '0');
                                                                                    const result = rob + bunkered + adjusted - consumed - debunkered;
                                                                                    LubeOilDebunkeringFormik?.setFieldValue(`lubeOil.${index}.LOROB`, result.toFixed(2));
                                                                                    if (e.target.value === '0') {
                                                                                        LubeOilDebunkeringFormik?.setFieldValue(`lubeOil.${index}.date_of_debunkering`, '');
                                                                                    }
                                                                                }}
                                                                            />
                                                                            <div className="input-group-text align-middle">ltr</div>
                                                                        </div>
                                                                        {LubeOilDebunkeringFormik?.errors?.lubeOil &&
                                                                            LubeOilDebunkeringFormik?.touched?.lubeOil?.[index]?.debunkered &&
                                                                            LubeOilDebunkeringFormik?.errors?.lubeOil?.[index]?.debunkered && !discardwarning &&
                                                                            env?.form_validation === true && (
                                                                                <ErrorTooltip
                                                                                    target={`debunkered-${index}`}
                                                                                    message={LubeOilDebunkeringFormik?.errors.lubeOil[index].debunkered}
                                                                                    open={true}
                                                                                />
                                                                            )}
                                                                        {LubeOilDebunkeringFormik?.errors?.lubeOil && LubeOilDebunkeringFormik?.errors?.lubeOil[index]?.LOROB && (
                                                                            <ErrorTooltip
                                                                                target={`debunkered-${index}`}
                                                                                message={LubeOilDebunkeringFormik?.errors?.lubeOil[index]?.LOROB}
                                                                                open={
                                                                                    LubeOilDebunkeringFormik?.errors?.lubeOil[index]?.LOROB
                                                                                        ? true
                                                                                        : false
                                                                                }
                                                                            />
                                                                        )}
                                                                    </td>
                                                                    {/* Date of Debunkering */}
                                                                    <td className="p-2 align-middle">
                                                                        <div className="d-flex justify-content-center">
                                                                            <input
                                                                                type="date"
                                                                                id={`date_of_debunkering-${index}`}
                                                                                name={`lubeOil.${index}.date_of_debunkering`}
                                                                                min={dateTimeFormat(previousVesselReport?.local_time, true)}
                                                                                max={dateTimeFormat(currentVesselReport?.local_time, true)}
                                                                                onBlur={LubeOilDebunkeringFormik?.handleBlur}
                                                                                onChange={LubeOilDebunkeringFormik?.handleChange}
                                                                                className={`datetimepicker text-uppercase form-control max-width-7
                                                                                    ${checkForNaN(LubeOilDebunkeringFormik?.values?.lubeOil[index]?.debunkered) === 0 ? "cursor-not-allowed" : ""}`}
                                                                                defaultValue={LubeOilDebunkeringFormik?.values?.lubeOil?.[index]?.date_of_debunkering}
                                                                                disabled={checkForNaN(LubeOilDebunkeringFormik?.values?.lubeOil[index]?.debunkered) <= 0}
                                                                            />
                                                                        </div>

                                                                        {LubeOilDebunkeringFormik?.errors?.lubeOil &&
                                                                            LubeOilDebunkeringFormik?.touched?.lubeOil?.[index]?.date_of_debunkering &&
                                                                            LubeOilDebunkeringFormik?.errors?.lubeOil?.[index]?.date_of_debunkering && !discardwarning &&
                                                                            env?.form_validation === true && (
                                                                                <ErrorTooltip
                                                                                    target={`date_of_debunkering-${index}`}
                                                                                    message={LubeOilDebunkeringFormik?.errors.lubeOil[index].date_of_debunkering}
                                                                                    open={true}
                                                                                />
                                                                            )}
                                                                    </td>
                                                                </tr>
                                                            </React.Fragment>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan={4} className="p-4 text-center">
                                                            <Message message="No Lube Oil configured" type="danger" />
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>

                                    </div>
                                </>
                            }
                        </Row>
                    </CardBody>
                    <CardFooter className="p-2 py-3">
                        <Row className="d-flex align-items-center flex-row-reverse">
                            <Col lg={10}>
                                <div className="d-flex flex-wrap gap-3 justify_right">
                                    {/* {editReportBool &&
                                        <Button
                                            type="reset"
                                            color="danger"
                                            className="btn_size_cstm"
                                            onClick={() => {
                                                setEditValues({
                                                    ...editValues,
                                                    chief_engineer: {
                                                        ...editValues?.chief_engineer,
                                                        lube_oil_bunkering: editInitialValues?.chief_engineer?.lube_oil_bunkering,
                                                    }
                                                })
                                                setRefreshKey(refreshKey + 1);
                                            }}
                                        >
                                            Reset
                                        </Button>
                                    } */}
                                    <Button
                                        type={editReportBool ? "button" : "submit"}
                                        color="primary"
                                        className="btn_size_cstm"
                                        disabled={LubeOilDebunkeringFormik?.isSubmitting || editIsSubmitting}
                                        onClick={async () => {
                                            const validationErrors = await LubeOilDebunkeringFormik?.validateForm();
                                            Object.keys(LubeOilDebunkeringFormik?.values || {}).forEach((key) => {
                                                const val = LubeOilDebunkeringFormik?.values[key];
                                                if (Array.isArray(val)) {
                                                    val.forEach((item, index) => {
                                                        Object.keys(item).forEach((field) => {
                                                            LubeOilDebunkeringFormik?.setFieldTouched(`${key}[${index}].${field}`, true, false);
                                                        });
                                                    });
                                                } else if (typeof val === "object" && val !== null) {
                                                    Object.keys(val).forEach((field) => {
                                                        LubeOilDebunkeringFormik?.setFieldTouched(`${key}.${field}`, true, false);
                                                    });
                                                } else {
                                                    LubeOilDebunkeringFormik?.setFieldTouched(key, true, false);
                                                }
                                            });
                                            if (editReportBool && Object.keys(validationErrors).length === 0) {
                                                await saveEditForm(editSubmit, toggleDynamicTabs, previousEnggTabIndex, setPreviousTabIndex);
                                                // setRefreshKey((prev: number) => prev + 1);
                                            }
                                        }}
                                    >
                                        {(LubeOilDebunkeringFormik?.isSubmitting || editIsSubmitting) && (
                                            <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                                        )}
                                        Next <i className="bx bx-chevron-right ms-1" />
                                    </Button>
                                </div>
                            </Col>
                            <Col lg={2}>
                                <Button
                                    type="button"
                                    color="primary"
                                    className="btn_size_cstm"
                                    onClick={() => {
                                        setErrorMessage(null);
                                        LubeOilDebunkeringFormik?.setErrors({});
                                        LubeOilDebunkeringFormik?.resetForm();
                                        toggleDynamicTabs(previousEnggTabIndex - 1);
                                    }}
                                >
                                    <i className="bx bx-chevron-left me-1" /> Previous
                                </Button>
                            </Col>
                        </Row>
                    </CardFooter>
                    <FormValuesDebug values={[LubeOilDebunkeringFormik?.values, LubeOilDebunkeringFormik?.errors, LubeOilDebunkeringFormik?.touched]} />
                </Form>
            }
        </Card>
    )
}

export default LubeOilDebunkeringComponent